export const ELEMENTS = [
  // ============================================
  // FOUNDATION BLOCK (F) - 15 Elements
  // ============================================
  {
    symbol: 'Pr',
    name: 'Perceptron',
    block: 'foundation',
    description: 'The original artificial neuron (Rosenblatt, 1958). A linear classifier that sparked the field.',
    year: 1958,
    keyPaper: 'Rosenblatt, F. (1958). The perceptron: a probabilistic model.',
  },
  {
    symbol: 'Bp',
    name: 'Backpropagation',
    block: 'foundation',
    description: 'The algorithm that enables learning by propagating errors backward through networks.',
    year: 1986,
    keyPaper: 'Rumelhart et al. (1986). Learning representations by back-propagating errors.',
  },
  {
    symbol: 'Gd',
    name: 'Gradient Descent',
    block: 'foundation',
    description: 'The optimization workhorse—iteratively adjusting parameters to minimize loss.',
    year: 1847,
    keyPaper: 'Cauchy, A. (1847). Méthode générale pour la résolution des systèmes d\'équations.',
  },
  {
    symbol: 'Af',
    name: 'Activation Functions',
    block: 'foundation',
    description: 'Non-linear functions (ReLU, sigmoid, tanh) that give networks their expressive power.',
    year: 1943,
    keyPaper: 'Various - ReLU popularized by Nair & Hinton (2010).',
  },
  {
    symbol: 'Ls',
    name: 'Loss Functions',
    block: 'foundation',
    description: 'Mathematical measures of "how wrong" a model is (MSE, cross-entropy, etc.).',
    year: null,
    keyPaper: 'Fundamental concept in optimization theory.',
  },
  {
    symbol: 'Wt',
    name: 'Weights',
    block: 'foundation',
    description: 'The learnable parameters connecting neurons—where knowledge is stored.',
    year: null,
    keyPaper: 'Core neural network concept.',
  },
  {
    symbol: 'Bi',
    name: 'Bias',
    block: 'foundation',
    description: 'Offset terms that allow neurons to shift their activation thresholds.',
    year: null,
    keyPaper: 'Core neural network concept.',
  },
  {
    symbol: 'Lr',
    name: 'Learning Rate',
    block: 'foundation',
    description: 'The step size for gradient descent—too high and you overshoot; too low and you crawl.',
    year: null,
    keyPaper: 'Fundamental hyperparameter in optimization.',
  },
  {
    symbol: 'Bn',
    name: 'Batch Normalization',
    block: 'foundation',
    description: 'Normalizing layer inputs to accelerate training and improve stability.',
    year: 2015,
    keyPaper: 'Ioffe & Szegedy (2015). Batch Normalization.',
  },
  {
    symbol: 'Do',
    name: 'Dropout',
    block: 'foundation',
    description: 'Randomly deactivating neurons during training to prevent overfitting.',
    year: 2014,
    keyPaper: 'Srivastava et al. (2014). Dropout: A Simple Way to Prevent Overfitting.',
  },
  {
    symbol: 'At',
    name: 'Attention',
    block: 'foundation',
    description: 'The mechanism that allows models to focus on relevant parts of input—the heart of transformers.',
    year: 2014,
    keyPaper: 'Bahdanau et al. (2014). Neural Machine Translation by Jointly Learning to Align.',
  },
  {
    symbol: 'Sf',
    name: 'Softmax',
    block: 'foundation',
    description: 'Converting raw scores into probabilities—the final layer of most classifiers.',
    year: 1959,
    keyPaper: 'Luce (1959). Individual Choice Behavior.',
  },
  {
    symbol: 'Em',
    name: 'Embeddings',
    block: 'foundation',
    description: 'Dense vector representations that capture semantic meaning.',
    year: 2013,
    keyPaper: 'Mikolov et al. (2013). Word2Vec.',
  },
  {
    symbol: 'Pe',
    name: 'Positional Encoding',
    block: 'foundation',
    description: 'Adding sequence order information to models (since transformers are order-agnostic).',
    year: 2017,
    keyPaper: 'Vaswani et al. (2017). Attention Is All You Need.',
  },
  {
    symbol: 'Tk',
    name: 'Tokenization',
    block: 'foundation',
    description: 'Breaking text into processable units (words, subwords, characters).',
    year: 2016,
    keyPaper: 'Sennrich et al. (2016). BPE Tokenization.',
  },

  // ============================================
  // ARCHITECTURE BLOCK (A) - 18 Elements
  // ============================================
  {
    symbol: 'Nn',
    name: 'Neural Network',
    block: 'architecture',
    description: 'The general class of connected, weighted computational graphs inspired by biological neurons.',
    year: 1943,
    keyPaper: 'McCulloch & Pitts (1943). A Logical Calculus of Ideas.',
  },
  {
    symbol: 'Ff',
    name: 'Feedforward',
    block: 'architecture',
    description: 'The simplest architecture: input → hidden layers → output, no cycles.',
    year: 1965,
    keyPaper: 'Ivakhnenko & Lapa (1965). Cybernetic Predicting Devices.',
  },
  {
    symbol: 'Cn',
    name: 'CNN',
    block: 'architecture',
    description: 'Convolutional Neural Networks—spatial hierarchies for images and signals.',
    year: 1989,
    keyPaper: 'LeCun et al. (1989). LeNet.',
  },
  {
    symbol: 'Rn',
    name: 'RNN',
    block: 'architecture',
    description: 'Recurrent Neural Networks—sequential processing with memory loops.',
    year: 1986,
    keyPaper: 'Rumelhart et al. (1986). Learning Internal Representations.',
  },
  {
    symbol: 'Ls',
    name: 'LSTM',
    block: 'architecture',
    description: 'Long Short-Term Memory—RNNs with gating mechanisms to handle long sequences.',
    year: 1997,
    keyPaper: 'Hochreiter & Schmidhuber (1997). Long Short-Term Memory.',
  },
  {
    symbol: 'Gu',
    name: 'GRU',
    block: 'architecture',
    description: 'Gated Recurrent Unit—a simplified LSTM variant with similar performance.',
    year: 2014,
    keyPaper: 'Cho et al. (2014). Learning Phrase Representations.',
  },
  {
    symbol: 'Ae',
    name: 'Autoencoder',
    block: 'architecture',
    description: 'Networks that compress then reconstruct input—learning efficient representations.',
    year: 1986,
    keyPaper: 'Rumelhart et al. (1986). Learning Internal Representations.',
  },
  {
    symbol: 'Va',
    name: 'VAE',
    block: 'architecture',
    description: 'Variational Autoencoders—probabilistic generative models with latent spaces.',
    year: 2013,
    keyPaper: 'Kingma & Welling (2013). Auto-Encoding Variational Bayes.',
  },
  {
    symbol: 'Gn',
    name: 'GAN',
    block: 'architecture',
    description: 'Generative Adversarial Networks—two networks competing to generate realistic data.',
    year: 2014,
    keyPaper: 'Goodfellow et al. (2014). Generative Adversarial Nets.',
  },
  {
    symbol: 'Tf',
    name: 'Transformer',
    block: 'architecture',
    description: 'Attention-based architecture that revolutionized NLP and beyond.',
    year: 2017,
    keyPaper: 'Vaswani et al. (2017). Attention Is All You Need.',
  },
  {
    symbol: 'Df',
    name: 'Diffusion',
    block: 'architecture',
    description: 'Models that generate data by learning to reverse a noise process.',
    year: 2020,
    keyPaper: 'Ho et al. (2020). Denoising Diffusion Probabilistic Models.',
  },
  {
    symbol: 'Mb',
    name: 'Mamba/SSM',
    block: 'architecture',
    description: 'State Space Models—efficient alternatives to transformers for long sequences.',
    year: 2023,
    keyPaper: 'Gu & Dao (2023). Mamba: Linear-Time Sequence Modeling.',
  },
  {
    symbol: 'Gp',
    name: 'Graph NN',
    block: 'architecture',
    description: 'Networks that operate on graph-structured data (social networks, molecules).',
    year: 2009,
    keyPaper: 'Scarselli et al. (2009). The Graph Neural Network Model.',
  },
  {
    symbol: 'Mo',
    name: 'Mixture of Experts',
    block: 'architecture',
    description: 'Architectures that route inputs to specialized sub-networks for efficiency.',
    year: 1991,
    keyPaper: 'Jacobs et al. (1991). Adaptive Mixtures of Local Experts.',
  },
  {
    symbol: 'Sp',
    name: 'Sparse Networks',
    block: 'architecture',
    description: 'Networks with selective activation for efficiency.',
    year: 2020,
    keyPaper: 'Fedus et al. (2022). Switch Transformers.',
  },
  {
    symbol: 'Hf',
    name: 'Hopfield',
    block: 'architecture',
    description: 'Historical associative memory networks (1982).',
    year: 1982,
    keyPaper: 'Hopfield (1982). Neural Networks and Physical Systems.',
  },
  {
    symbol: 'Bm',
    name: 'Boltzmann Machine',
    block: 'architecture',
    description: 'Historical probabilistic networks with symmetric connections.',
    year: 1985,
    keyPaper: 'Ackley et al. (1985). A Learning Algorithm for Boltzmann Machines.',
  },
  {
    symbol: 'Rb',
    name: 'RBM',
    block: 'architecture',
    description: 'Restricted Boltzmann Machines—foundational for deep belief networks.',
    year: 2006,
    keyPaper: 'Hinton et al. (2006). A Fast Learning Algorithm for Deep Belief Nets.',
  },

  // ============================================
  // MODELS BLOCK (M) - 15 Elements
  // ============================================
  {
    symbol: 'Lm',
    name: 'Language Model',
    block: 'models',
    description: 'Models that understand and generate text (GPT, Claude, LLaMA, Gemini).',
    year: 2018,
    keyPaper: 'Radford et al. (2018). GPT.',
  },
  {
    symbol: 'Vm',
    name: 'Vision Model',
    block: 'models',
    description: 'Models that understand images (CLIP, ViT, ResNet, DINOv2).',
    year: 2012,
    keyPaper: 'Krizhevsky et al. (2012). AlexNet.',
  },
  {
    symbol: 'Im',
    name: 'Image Generator',
    block: 'models',
    description: 'Models that create images (Stable Diffusion, DALL-E, Midjourney, Imagen).',
    year: 2021,
    keyPaper: 'Ramesh et al. (2021). DALL-E.',
  },
  {
    symbol: 'Am',
    name: 'Audio Model',
    block: 'models',
    description: 'Models for speech recognition and synthesis (Whisper, AudioLM, Bark).',
    year: 2022,
    keyPaper: 'Radford et al. (2022). Whisper.',
  },
  {
    symbol: 'Vd',
    name: 'Video Model',
    block: 'models',
    description: 'Models that understand or generate video (Sora, Runway, Pika).',
    year: 2024,
    keyPaper: 'OpenAI (2024). Sora.',
  },
  {
    symbol: 'Mm',
    name: 'Multimodal',
    block: 'models',
    description: 'Models that process multiple modalities together (GPT-4V, Gemini, Claude 3).',
    year: 2023,
    keyPaper: 'OpenAI (2023). GPT-4V.',
  },
  {
    symbol: 'Cd',
    name: 'Code Model',
    block: 'models',
    description: 'Specialized models for programming (Codex, CodeLLaMA, DeepSeek Coder).',
    year: 2021,
    keyPaper: 'Chen et al. (2021). Codex.',
  },
  {
    symbol: 'Eb',
    name: 'Embodied AI',
    block: 'models',
    description: 'Models controlling physical robots or simulated agents.',
    year: 2023,
    keyPaper: 'Various robotics foundation model work.',
  },
  {
    symbol: 'Sl',
    name: 'Small LM',
    block: 'models',
    description: 'Efficient, deployable language models (Phi, Gemma, Mistral 7B).',
    year: 2023,
    keyPaper: 'Microsoft (2023). Phi.',
  },
  {
    symbol: 'Wm',
    name: 'World Model',
    block: 'models',
    description: 'Models that learn physics and environmental dynamics for planning.',
    year: 2018,
    keyPaper: 'Ha & Schmidhuber (2018). World Models.',
  },
  {
    symbol: 'Sc',
    name: 'Scientific Model',
    block: 'models',
    description: 'Domain-specific models (AlphaFold for proteins, GNoME for materials).',
    year: 2020,
    keyPaper: 'Jumper et al. (2021). AlphaFold 2.',
  },
  {
    symbol: '3D',
    name: '3D Model',
    block: 'models',
    description: 'Models for 3D generation and understanding (NeRF, 3D Gaussian Splatting).',
    year: 2020,
    keyPaper: 'Mildenhall et al. (2020). NeRF.',
  },
  {
    symbol: 'Au',
    name: 'Audio',
    block: 'models',
    description: 'Models for audio generation and understanding.',
    year: 2023,
    keyPaper: 'Various audio generation models.',
  },
  {
    symbol: '3C',
    name: 'Scientific',
    block: 'models',
    description: 'Scientific computing and simulation models.',
    year: 2021,
    keyPaper: 'DeepMind (2021). AlphaFold.',
  },

  // ============================================
  // TRAINING BLOCK (T) - 16 Elements
  // ============================================
  {
    symbol: 'Sv',
    name: 'Supervised',
    block: 'training',
    description: 'Learning from labeled input-output pairs.',
    year: 1950,
    keyPaper: 'Classical machine learning approach.',
  },
  {
    symbol: 'Us',
    name: 'Unsupervised',
    block: 'training',
    description: 'Finding patterns in unlabeled data.',
    year: 1960,
    keyPaper: 'Classical machine learning approach.',
  },
  {
    symbol: 'Ss',
    name: 'Self-Supervised',
    block: 'training',
    description: 'Creating labels from the data itself (next-token prediction, masked modeling).',
    year: 2018,
    keyPaper: 'Devlin et al. (2018). BERT.',
  },
  {
    symbol: 'Rl',
    name: 'Reinforcement',
    block: 'training',
    description: 'Learning from rewards and punishments through trial and error.',
    year: 1989,
    keyPaper: 'Watkins (1989). Q-Learning.',
  },
  {
    symbol: 'Rh',
    name: 'RLHF',
    block: 'training',
    description: 'Reinforcement Learning from Human Feedback—aligning models to human preferences.',
    year: 2017,
    keyPaper: 'Christiano et al. (2017). Deep RL from Human Preferences.',
  },
  {
    symbol: 'Dp',
    name: 'DPO',
    block: 'training',
    description: 'Direct Preference Optimization—simplified alternative to RLHF.',
    year: 2023,
    keyPaper: 'Rafailov et al. (2023). Direct Preference Optimization.',
  },
  {
    symbol: 'Pt',
    name: 'Pre-training',
    block: 'training',
    description: 'Large-scale initial training on broad data.',
    year: 2018,
    keyPaper: 'Devlin et al. (2018). BERT.',
  },
  {
    symbol: 'Ft',
    name: 'Fine-tuning',
    block: 'training',
    description: 'Adapting pre-trained models to specific tasks or domains.',
    year: 2018,
    keyPaper: 'Howard & Ruder (2018). ULMFiT.',
  },
  {
    symbol: 'Lo',
    name: 'LoRA',
    block: 'training',
    description: 'Low-Rank Adaptation—efficient fine-tuning by training small adapter layers.',
    year: 2021,
    keyPaper: 'Hu et al. (2021). LoRA.',
  },
  {
    symbol: 'Tl',
    name: 'Transfer Learning',
    block: 'training',
    description: 'Applying knowledge from one domain to another.',
    year: 1976,
    keyPaper: 'Concept from cognitive psychology, ML since 1990s.',
  },
  {
    symbol: 'Mt',
    name: 'Meta-Learning',
    block: 'training',
    description: 'Learning to learn—training systems that quickly adapt to new tasks.',
    year: 2017,
    keyPaper: 'Finn et al. (2017). MAML.',
  },
  {
    symbol: 'Cl',
    name: 'Curriculum Learning',
    block: 'training',
    description: 'Training on progressively harder examples.',
    year: 2009,
    keyPaper: 'Bengio et al. (2009). Curriculum Learning.',
  },
  {
    symbol: 'Ct',
    name: 'Contrastive',
    block: 'training',
    description: 'Learning by comparing similar and dissimilar examples.',
    year: 2020,
    keyPaper: 'Chen et al. (2020). SimCLR.',
  },
  {
    symbol: 'Fl',
    name: 'Federated',
    block: 'training',
    description: 'Distributed training across devices without centralizing data.',
    year: 2017,
    keyPaper: 'McMahan et al. (2017). Communication-Efficient Learning.',
  },
  {
    symbol: 'Kd',
    name: 'Distillation',
    block: 'training',
    description: 'Transferring knowledge from large models to smaller ones.',
    year: 2015,
    keyPaper: 'Hinton et al. (2015). Distilling Knowledge.',
  },
  {
    symbol: 'Ds',
    name: 'Distributed',
    block: 'training',
    description: 'Parallel training across multiple machines/GPUs.',
    year: 2012,
    keyPaper: 'Dean et al. (2012). Large Scale Distributed Deep Networks.',
  },

  // ============================================
  // TECHNIQUES BLOCK (Tc) - 18 Elements
  // ============================================
  {
    symbol: 'Pm',
    name: 'Prompting',
    block: 'techniques',
    description: 'Crafting inputs to elicit desired model behavior.',
    year: 2020,
    keyPaper: 'Brown et al. (2020). GPT-3.',
  },
  {
    symbol: 'Co',
    name: 'Chain-of-Thought',
    block: 'techniques',
    description: 'Prompting models to show reasoning steps before answering.',
    year: 2022,
    keyPaper: 'Wei et al. (2022). Chain-of-Thought Prompting.',
  },
  {
    symbol: 'Rg',
    name: 'RAG',
    block: 'techniques',
    description: 'Retrieval-Augmented Generation—fetching relevant context before generation.',
    year: 2020,
    keyPaper: 'Lewis et al. (2020). RAG.',
  },
  {
    symbol: 'Fc',
    name: 'Function Calling',
    block: 'techniques',
    description: 'Enabling models to invoke external tools and APIs.',
    year: 2023,
    keyPaper: 'OpenAI (2023). Function Calling.',
  },
  {
    symbol: 'Ic',
    name: 'In-Context Learning',
    block: 'techniques',
    description: 'Teaching models through examples in the prompt.',
    year: 2020,
    keyPaper: 'Brown et al. (2020). GPT-3.',
  },
  {
    symbol: 'Fs',
    name: 'Few-Shot',
    block: 'techniques',
    description: 'Learning from a handful of examples.',
    year: 2020,
    keyPaper: 'Brown et al. (2020). GPT-3.',
  },
  {
    symbol: 'Zs',
    name: 'Zero-Shot',
    block: 'techniques',
    description: 'Performing tasks with no examples, only instructions.',
    year: 2019,
    keyPaper: 'Radford et al. (2019). GPT-2.',
  },
  {
    symbol: 'Ag',
    name: 'Agents',
    block: 'techniques',
    description: 'Autonomous systems that perceive, reason, and act in loops.',
    year: 2023,
    keyPaper: 'Yao et al. (2023). ReAct.',
  },
  {
    symbol: 'Ma',
    name: 'Multi-Agent',
    block: 'techniques',
    description: 'Multiple agents collaborating or competing.',
    year: 2023,
    keyPaper: 'Various multi-agent frameworks.',
  },
  {
    symbol: 'Or',
    name: 'Orchestration',
    block: 'techniques',
    description: 'Coordinating multiple components, models, or agents.',
    year: 2023,
    keyPaper: 'LangChain, LlamaIndex frameworks.',
  },
  {
    symbol: 'Mc',
    name: 'MCP',
    block: 'techniques',
    description: 'Model Context Protocol—standardized tool integration.',
    year: 2024,
    keyPaper: 'Anthropic (2024). MCP.',
  },
  {
    symbol: 'Tu',
    name: 'Tool Use',
    block: 'techniques',
    description: 'Models invoking calculators, databases, APIs, etc.',
    year: 2023,
    keyPaper: 'Schick et al. (2023). Toolformer.',
  },
  {
    symbol: 'Pl',
    name: 'Planning',
    block: 'techniques',
    description: 'Decomposing goals into actionable sequences.',
    year: 2023,
    keyPaper: 'Yao et al. (2023). Tree of Thoughts.',
  },
  {
    symbol: 'Me',
    name: 'Memory',
    block: 'techniques',
    description: 'Persisting information across interactions.',
    year: 2023,
    keyPaper: 'Various memory-augmented architectures.',
  },
  {
    symbol: 'Gr',
    name: 'Grounding',
    block: 'techniques',
    description: 'Anchoring model outputs to factual sources.',
    year: 2020,
    keyPaper: 'RAG and attribution research.',
  },
  {
    symbol: 'Re',
    name: 'Reflection',
    block: 'techniques',
    description: 'Models evaluating and refining their own outputs.',
    year: 2023,
    keyPaper: 'Shinn et al. (2023). Reflexion.',
  },

  // ============================================
  // EVALUATION BLOCK (E) - 12 Elements
  // ============================================
  {
    symbol: 'Lx',
    name: 'Language Metrics',
    block: 'evaluation',
    description: 'BLEU, ROUGE, perplexity—measuring text quality.',
    year: 2002,
    keyPaper: 'Papineni et al. (2002). BLEU.',
  },
  {
    symbol: 'Cx',
    name: 'Code Metrics',
    block: 'evaluation',
    description: 'HumanEval, MBPP, SWE-bench—measuring coding ability.',
    year: 2021,
    keyPaper: 'Chen et al. (2021). HumanEval.',
  },
  {
    symbol: 'Rx',
    name: 'Reasoning Metrics',
    block: 'evaluation',
    description: 'MMLU, GSM8K, ARC, GPQA—measuring logical capabilities.',
    year: 2021,
    keyPaper: 'Hendrycks et al. (2021). MMLU.',
  },
  {
    symbol: 'Vx',
    name: 'Vision Metrics',
    block: 'evaluation',
    description: 'FID, CLIP Score, ImageNet accuracy—measuring visual understanding.',
    year: 2009,
    keyPaper: 'Deng et al. (2009). ImageNet.',
  },
  {
    symbol: 'Sx',
    name: 'Safety Metrics',
    block: 'evaluation',
    description: 'TruthfulQA, toxicity scores, refusal rates—measuring alignment.',
    year: 2022,
    keyPaper: 'Lin et al. (2022). TruthfulQA.',
  },
  {
    symbol: 'Ex',
    name: 'Efficiency Metrics',
    block: 'evaluation',
    description: 'Latency, throughput, memory footprint, FLOPS.',
    year: null,
    keyPaper: 'Standard engineering metrics.',
  },
  {
    symbol: 'Hx',
    name: 'Human Eval',
    block: 'evaluation',
    description: 'Elo ratings, preference comparisons, user studies (Chatbot Arena).',
    year: 2023,
    keyPaper: 'Zheng et al. (2023). Chatbot Arena.',
  },
  {
    symbol: 'Ax',
    name: 'Agentic Metrics',
    block: 'evaluation',
    description: 'Task completion rates, tool use accuracy, multi-step success.',
    year: 2023,
    keyPaper: 'Various agent benchmarks.',
  },
  {
    symbol: 'Bx',
    name: 'Behavioral Metrics',
    block: 'evaluation',
    description: 'Consistency, calibration, instruction following.',
    year: 2023,
    keyPaper: 'Various alignment benchmarks.',
  },

  // ============================================
  // COGNITION BLOCK (C) - 17 Elements
  // ============================================
  {
    symbol: 'Rs',
    name: 'Reasoning',
    block: 'cognition',
    description: 'Drawing logical conclusions from premises.',
    year: null,
    keyPaper: 'Fundamental cognitive capability.',
  },
  {
    symbol: 'Th',
    name: 'Thinking',
    block: 'cognition',
    description: 'Deliberate, multi-step cognitive processing.',
    year: 2024,
    keyPaper: 'OpenAI (2024). o1 thinking models.',
  },
  {
    symbol: 'Pl',
    name: 'Planning',
    block: 'cognition',
    description: 'Formulating sequences of actions toward goals.',
    year: null,
    keyPaper: 'Fundamental cognitive capability.',
  },
  {
    symbol: 'Cr',
    name: 'Creativity',
    block: 'cognition',
    description: 'Generating novel, valuable ideas or artifacts.',
    year: null,
    keyPaper: 'Philosophical concept, hard to measure.',
  },
  {
    symbol: 'Cu',
    name: 'Curiosity',
    block: 'cognition',
    description: 'Self-directed exploration and question generation.',
    year: 2017,
    keyPaper: 'Pathak et al. (2017). Curiosity-driven Exploration.',
  },
  {
    symbol: 'Ep',
    name: 'Empathy',
    block: 'cognition',
    description: 'Understanding and sharing others\' emotional states.',
    year: null,
    keyPaper: 'Simulated in models, not experienced.',
  },
  {
    symbol: 'In',
    name: 'Intuition',
    block: 'cognition',
    description: 'Rapid pattern recognition without explicit reasoning.',
    year: null,
    keyPaper: 'System 1 thinking concept.',
  },
  {
    symbol: 'Ab',
    name: 'Abstraction',
    block: 'cognition',
    description: 'Extracting general principles from specific instances.',
    year: null,
    keyPaper: 'Chollet (2019). ARC benchmark.',
  },
  {
    symbol: 'An',
    name: 'Analogy',
    block: 'cognition',
    description: 'Mapping relationships between different domains.',
    year: null,
    keyPaper: 'Hofstadter\'s work on analogy.',
  },
  {
    symbol: 'Gz',
    name: 'Generalization',
    block: 'cognition',
    description: 'Applying learned knowledge to novel situations.',
    year: null,
    keyPaper: 'Core ML concept.',
  },
  {
    symbol: 'Cs',
    name: 'Common Sense',
    block: 'cognition',
    description: 'Implicit understanding of how the world works.',
    year: 2019,
    keyPaper: 'Zellers et al. (2019). HellaSwag.',
  },
  {
    symbol: 'Lg',
    name: 'Logic',
    block: 'cognition',
    description: 'Formal deductive and inductive reasoning.',
    year: null,
    keyPaper: 'Fundamental cognitive capability.',
  },
  {
    symbol: 'Mm',
    name: 'Memory',
    block: 'cognition',
    description: 'Encoding, storing, and retrieving information.',
    year: null,
    keyPaper: 'Core cognitive capability.',
  },
  {
    symbol: 'Fo',
    name: 'Focus',
    block: 'cognition',
    description: 'Selectively attending to relevant information.',
    year: 2014,
    keyPaper: 'Attention mechanisms.',
  },
  {
    symbol: 'Ln',
    name: 'Learning',
    block: 'cognition',
    description: 'Acquiring new knowledge and skills from experience.',
    year: null,
    keyPaper: 'Core AI capability.',
  },
  {
    symbol: 'Ad',
    name: 'Adaptation',
    block: 'cognition',
    description: 'Adjusting behavior based on changing circumstances.',
    year: null,
    keyPaper: 'Meta-learning research.',
  },
  {
    symbol: 'Mc',
    name: 'Metacognition',
    block: 'cognition',
    description: 'Thinking about one\'s own thinking processes.',
    year: 2024,
    keyPaper: 'Emerging in reasoning models.',
  },

  // ============================================
  // SAFETY BLOCK (S) - 16 Elements
  // ============================================
  {
    symbol: 'Gr',
    name: 'Guardrails',
    block: 'safety',
    description: 'Hard constraints on model behavior (content filters, output validation).',
    year: 2022,
    keyPaper: 'Various safety systems.',
  },
  {
    symbol: 'Al',
    name: 'Alignment',
    block: 'safety',
    description: 'Training models to pursue human-intended goals.',
    year: 2017,
    keyPaper: 'Christiano et al. (2017). AI Alignment.',
  },
  {
    symbol: 'Rt',
    name: 'Red Teaming',
    block: 'safety',
    description: 'Adversarially probing systems for vulnerabilities.',
    year: 2022,
    keyPaper: 'Ganguli et al. (2022). Red Teaming.',
  },
  {
    symbol: 'Ip',
    name: 'Interpretability',
    block: 'safety',
    description: 'Understanding why models produce specific outputs.',
    year: 2017,
    keyPaper: 'Various mechanistic interpretability work.',
  },
  {
    symbol: 'Xp',
    name: 'Explainability',
    block: 'safety',
    description: 'Communicating model decisions to users.',
    year: 2016,
    keyPaper: 'Ribeiro et al. (2016). LIME.',
  },
  {
    symbol: 'Hl',
    name: 'Hallucination',
    block: 'safety',
    description: '(Anti-element) Generating false information confidently.',
    year: 2020,
    keyPaper: 'Problem identified in generative models.',
  },
  {
    symbol: 'Ca',
    name: 'Constitutional AI',
    block: 'safety',
    description: 'Training models with explicit behavioral principles.',
    year: 2022,
    keyPaper: 'Bai et al. (2022). Constitutional AI.',
  },
  {
    symbol: 'Vl',
    name: 'Value Learning',
    block: 'safety',
    description: 'Inferring human values from behavior and feedback.',
    year: 2016,
    keyPaper: 'Hadfield-Menell et al. (2016). CIRL.',
  },
  {
    symbol: 'Ro',
    name: 'Robustness',
    block: 'safety',
    description: 'Maintaining correct behavior under adversarial inputs.',
    year: 2014,
    keyPaper: 'Szegedy et al. (2014). Adversarial Examples.',
  },
  {
    symbol: 'Pv',
    name: 'Privacy',
    block: 'safety',
    description: 'Protecting sensitive information in training and inference.',
    year: 2017,
    keyPaper: 'Abadi et al. (2016). Deep Learning with Differential Privacy.',
  },
  {
    symbol: 'Fr',
    name: 'Fairness',
    block: 'safety',
    description: 'Ensuring equitable treatment across demographic groups.',
    year: 2016,
    keyPaper: 'Various fairness in ML research.',
  },
  {
    symbol: 'Bm',
    name: 'Bias Mitigation',
    block: 'safety',
    description: 'Reducing systematic errors and stereotypes.',
    year: 2016,
    keyPaper: 'Bolukbasi et al. (2016). Man is to Computer Programmer.',
  },
  {
    symbol: 'Se',
    name: 'Security',
    block: 'safety',
    description: 'Protecting against attacks, jailbreaks, and prompt injection.',
    year: 2023,
    keyPaper: 'Various prompt injection research.',
  },
  {
    symbol: 'Au',
    name: 'Auditing',
    block: 'safety',
    description: 'Systematic evaluation of model behavior and impacts.',
    year: 2020,
    keyPaper: 'Model cards, datasheets for datasets.',
  },
  {
    symbol: 'Tr',
    name: 'Transparency',
    block: 'safety',
    description: 'Making model capabilities and limitations clear.',
    year: 2019,
    keyPaper: 'Mitchell et al. (2019). Model Cards.',
  },
  {
    symbol: 'Ct',
    name: 'Containment',
    block: 'safety',
    description: '(Future) Ensuring superintelligent systems remain controllable.',
    year: null,
    keyPaper: 'Theoretical alignment research.',
  },

  // ============================================
  // INFRASTRUCTURE BLOCK (I) - 15 Elements
  // ============================================
  {
    symbol: 'Vb',
    name: 'Vector DB',
    block: 'infrastructure',
    description: 'Databases optimized for similarity search (Pinecone, Weaviate, Qdrant).',
    year: 2021,
    keyPaper: 'Various vector database implementations.',
  },
  {
    symbol: 'Fw',
    name: 'Frameworks',
    block: 'infrastructure',
    description: 'Development scaffolding (LangChain, LlamaIndex, DSPy).',
    year: 2022,
    keyPaper: 'LangChain, LlamaIndex frameworks.',
  },
  {
    symbol: 'Oc',
    name: 'Orchestration',
    block: 'infrastructure',
    description: 'Managing complex AI workflows and pipelines.',
    year: 2023,
    keyPaper: 'Various orchestration frameworks.',
  },
  {
    symbol: 'Sv',
    name: 'Serving',
    block: 'infrastructure',
    description: 'Deploying models for inference (vLLM, TensorRT, Triton).',
    year: 2023,
    keyPaper: 'vLLM, TGI serving systems.',
  },
  {
    symbol: 'Sc',
    name: 'Scaling',
    block: 'infrastructure',
    description: 'Handling increased load and model size.',
    year: 2020,
    keyPaper: 'Kaplan et al. (2020). Scaling Laws.',
  },
  {
    symbol: 'Di',
    name: 'Distributed',
    block: 'infrastructure',
    description: 'Parallel computation across multiple machines.',
    year: 2012,
    keyPaper: 'Dean et al. (2012). DistBelief.',
  },
  {
    symbol: 'Hw',
    name: 'Hardware',
    block: 'infrastructure',
    description: 'GPUs, TPUs, custom accelerators (NVIDIA, AMD, custom ASICs).',
    year: 2012,
    keyPaper: 'GPU computing for deep learning.',
  },
  {
    symbol: 'Op',
    name: 'Optimization',
    block: 'infrastructure',
    description: 'Making inference faster and cheaper.',
    year: 2020,
    keyPaper: 'Various optimization techniques.',
  },
  {
    symbol: 'Qn',
    name: 'Quantization',
    block: 'infrastructure',
    description: 'Reducing numerical precision for efficiency.',
    year: 2020,
    keyPaper: 'Various quantization methods.',
  },
  {
    symbol: 'Pu',
    name: 'Pruning',
    block: 'infrastructure',
    description: 'Removing unnecessary model parameters.',
    year: 2015,
    keyPaper: 'Han et al. (2015). Deep Compression.',
  },
  {
    symbol: 'Ed',
    name: 'Edge',
    block: 'infrastructure',
    description: 'Deploying AI on devices rather than cloud.',
    year: 2017,
    keyPaper: 'Various edge AI implementations.',
  },
  {
    symbol: 'Ap',
    name: 'APIs',
    block: 'infrastructure',
    description: 'Programmatic interfaces to AI services.',
    year: 2020,
    keyPaper: 'OpenAI API, Anthropic API, etc.',
  },
  {
    symbol: 'Sd',
    name: 'SDKs',
    block: 'infrastructure',
    description: 'Developer toolkits for AI integration.',
    year: 2020,
    keyPaper: 'Various AI SDKs.',
  },
  {
    symbol: 'Mn',
    name: 'Monitoring',
    block: 'infrastructure',
    description: 'Observability for AI systems in production.',
    year: 2020,
    keyPaper: 'MLOps observability tools.',
  },
  {
    symbol: 'Ml',
    name: 'MLOps',
    block: 'infrastructure',
    description: 'Operational practices for ML lifecycle management.',
    year: 2019,
    keyPaper: 'MLOps best practices.',
  },

  // ============================================
  // DATA BLOCK (D) - 15 Elements
  // ============================================
  {
    symbol: 'Td',
    name: 'Training Data',
    block: 'data',
    description: 'The raw material for model learning.',
    year: null,
    keyPaper: 'Fundamental to all ML.',
  },
  {
    symbol: 'Sy',
    name: 'Synthetic Data',
    block: 'data',
    description: 'AI-generated data for training AI.',
    year: 2023,
    keyPaper: 'Various synthetic data generation methods.',
  },
  {
    symbol: 'An',
    name: 'Annotation',
    block: 'data',
    description: 'Human labeling of data for supervised learning.',
    year: null,
    keyPaper: 'Fundamental to supervised ML.',
  },
  {
    symbol: 'Cu',
    name: 'Curation',
    block: 'data',
    description: 'Selecting and organizing quality data.',
    year: 2020,
    keyPaper: 'Data quality research.',
  },
  {
    symbol: 'Cl',
    name: 'Cleaning',
    block: 'data',
    description: 'Removing errors, duplicates, and harmful content.',
    year: null,
    keyPaper: 'Data preprocessing best practices.',
  },
  {
    symbol: 'Au',
    name: 'Augmentation',
    block: 'data',
    description: 'Creating variations of existing data to increase diversity.',
    year: 2012,
    keyPaper: 'Various data augmentation techniques.',
  },
  {
    symbol: 'Kg',
    name: 'Knowledge Graph',
    block: 'data',
    description: 'Structured representations of entity relationships.',
    year: 2012,
    keyPaper: 'Google (2012). Knowledge Graph.',
  },
  {
    symbol: 'Db',
    name: 'Databases',
    block: 'data',
    description: 'Structured storage systems for AI data.',
    year: null,
    keyPaper: 'Database systems.',
  },
  {
    symbol: 'St',
    name: 'Structured',
    block: 'data',
    description: 'Data with explicit schema (tables, JSON, XML).',
    year: null,
    keyPaper: 'Data engineering concepts.',
  },
  {
    symbol: 'Us',
    name: 'Unstructured',
    block: 'data',
    description: 'Free-form data (text documents, images, audio).',
    year: null,
    keyPaper: 'Data engineering concepts.',
  },
  {
    symbol: 'Tx',
    name: 'Text Data',
    block: 'data',
    description: 'Written content for language model training.',
    year: null,
    keyPaper: 'Common Crawl, web data.',
  },
  {
    symbol: 'Ig',
    name: 'Image Data',
    block: 'data',
    description: 'Visual content for vision model training.',
    year: 2009,
    keyPaper: 'ImageNet, LAION.',
  },
  {
    symbol: 'Vd',
    name: 'Video Data',
    block: 'data',
    description: 'Temporal visual content.',
    year: 2017,
    keyPaper: 'Kinetics, YouTube datasets.',
  },
  {
    symbol: 'Ad',
    name: 'Audio Data',
    block: 'data',
    description: 'Sound and speech recordings.',
    year: 2015,
    keyPaper: 'LibriSpeech, Common Voice.',
  },
  {
    symbol: 'Md',
    name: 'Multimodal Data',
    block: 'data',
    description: 'Aligned data across multiple modalities.',
    year: 2021,
    keyPaper: 'LAION-5B, WebVid.',
  },

  // ============================================
  // FUTURE BLOCK (X) - 14 Elements
  // ============================================
  {
    symbol: 'Agi',
    name: 'AGI',
    block: 'future',
    description: 'Artificial General Intelligence—systems matching human cognitive breadth.',
    year: null,
    keyPaper: 'Theoretical - not yet achieved.',
    status: 'theoretical',
  },
  {
    symbol: 'Asi',
    name: 'ASI',
    block: 'future',
    description: 'Artificial Superintelligence—systems exceeding human capabilities.',
    year: null,
    keyPaper: 'Speculative - Bostrom (2014). Superintelligence.',
    status: 'speculative',
  },
  {
    symbol: 'Si',
    name: 'Self-Improve',
    block: 'future',
    description: 'Systems that enhance their own capabilities autonomously.',
    year: null,
    keyPaper: 'Early research phase.',
    status: 'research',
  },
  {
    symbol: 'Rc',
    name: 'Recursive',
    block: 'future',
    description: 'Self-improvement that compounds—improving the improvement process.',
    year: null,
    keyPaper: 'Speculative concept.',
    status: 'speculative',
  },
  {
    symbol: 'Cn',
    name: 'Consciousness',
    block: 'future',
    description: 'Subjective experience in artificial systems.',
    year: null,
    keyPaper: 'Philosophical - deeply contested.',
    status: 'philosophical',
  },
  {
    symbol: 'Sa',
    name: 'Self-Aware',
    block: 'future',
    description: 'Systems with models of themselves and their place in the world.',
    year: null,
    keyPaper: 'Theoretical concept.',
    status: 'theoretical',
  },
  {
    symbol: 'Um',
    name: 'Universal',
    block: 'future',
    description: 'Models that can learn any learnable task.',
    year: null,
    keyPaper: 'Theoretical - universal approximation.',
    status: 'theoretical',
  },
  {
    symbol: 'Nm',
    name: 'Neuromorphic',
    block: 'future',
    description: 'Hardware mimicking biological neural structures.',
    year: 2014,
    keyPaper: 'Intel Loihi, IBM TrueNorth.',
    status: 'emerging',
  },
  {
    symbol: 'Qm',
    name: 'Quantum ML',
    block: 'future',
    description: 'Machine learning on quantum computers.',
    year: 2019,
    keyPaper: 'Various quantum ML research.',
    status: 'research',
  },
  {
    symbol: 'Bc',
    name: 'Brain-Computer',
    block: 'future',
    description: 'Direct neural interfaces for AI collaboration.',
    year: 2021,
    keyPaper: 'Neuralink, BCI research.',
    status: 'emerging',
  },
  {
    symbol: 'Pm',
    name: 'Persistent',
    block: 'future',
    description: 'AI systems that maintain continuous memory and identity.',
    year: 2024,
    keyPaper: 'Emerging capability in agents.',
    status: 'research',
  },
  {
    symbol: 'Sm',
    name: 'Simulation',
    block: 'future',
    description: 'AI within simulated environments for accelerated learning.',
    year: 2019,
    keyPaper: 'OpenAI Five, AlphaStar.',
    status: 'active',
  },
  {
    symbol: 'Ex',
    name: 'X-Risk',
    block: 'future',
    description: 'Existential risk from advanced AI systems.',
    year: 2014,
    keyPaper: 'Bostrom (2014), AI safety research.',
    status: 'concern',
  },
  {
    symbol: 'Sg',
    name: 'Singularity',
    block: 'future',
    description: 'Point of runaway technological growth.',
    year: 1993,
    keyPaper: 'Vinge (1993). The Coming Technological Singularity.',
    status: 'speculative',
  },
];

export const getElementsByBlock = (blockId) => {
  return ELEMENTS.filter(el => el.block === blockId);
};

export const getElementBySymbol = (symbol) => {
  return ELEMENTS.find(el => el.symbol.toLowerCase() === symbol.toLowerCase());
};

export const searchElements = (query) => {
  const q = query.toLowerCase();
  return ELEMENTS.filter(el => 
    el.symbol.toLowerCase().includes(q) ||
    el.name.toLowerCase().includes(q) ||
    el.description.toLowerCase().includes(q)
  );
};

export default ELEMENTS;
